import { ref } from 'vue';

import { getActivityEntry, reportNewbieTaskView } from '@/jmHttp/api/activityNbi';

/**
 * @file 活动入口可见性
 * @module common/hooks/useActivityEntry
 * 顶栏「任务」入口与头像浮层「每日签到奖励」卡片共用同一份可见性数据，
 * 因此状态提升到模块级，多个组件挂载只会发起一次请求。
 */

/** 接口异常或未登录时的兜底：一律不展示活动入口 */
const DEFAULT_ENTRY = {
  signInEntry: false,
  newbieEntry: false,
  signInEntryUrl: '',
  newbieEntryUrl: '',
  newbieTaskRedDot: false
};

const activityEntry = ref({ ...DEFAULT_ENTRY });

/** 飞行中的请求，用于并发去重 */
let inflight = null;
/** 是否已成功加载过，避免重复请求 */
let loaded = false;
/** 用于识别退出登录前发起的过期请求 */
let requestVersion = 0;

/**
 * 拉取当前用户各活动入口的可见性，结果在模块级缓存。
 * @param {boolean} [force] - 是否忽略缓存强制刷新（如切换账号后）
 * @returns {Promise<{ signInEntry: boolean, newbieEntry: boolean, signInEntryUrl: string, newbieEntryUrl: string, newbieTaskRedDot: boolean }>}
 */
export async function fetchActivityEntry(force = false) {
  // 已加载且非强制刷新时直接复用缓存，避免头像浮层每次 hover 都发请求
  if (loaded && !force) return activityEntry.value;
  if (inflight) return inflight;

  const currentRequestVersion = requestVersion;
  const request = getActivityEntry({}, { hideMessage: true })
    .then((res) => {
      // 退出登录后旧请求返回时不可覆盖新账号的活动入口状态
      if (currentRequestVersion !== requestVersion) return activityEntry.value;
      const data = res?.data || {};
      activityEntry.value = {
        signInEntry: !!data.signInEntry,
        newbieEntry: !!data.newbieEntry,
        signInEntryUrl: data.signInEntryUrl || '',
        newbieEntryUrl: data.newbieEntryUrl || '',
        newbieTaskRedDot: !!data.newbieTaskRedDot
      };
      loaded = true;
      return activityEntry.value;
    })
    .catch((err) => {
      console.error('查询活动入口可见性失败：', err);
      return activityEntry.value;
    })
    .finally(() => {
      // 只清除当前请求，避免旧请求结束时误清除新账号的请求引用
      if (inflight === request) inflight = null;
    });

  inflight = request;
  return request;
}

/**
 * 标记用户已查看新上线的新手任务：先就地消掉红点再上报，
 * 避免等接口返回才收起气泡；上报失败时下次进入仍会由后端重新下发红点。
 * @returns {Promise<void>}
 */
export async function markNewbieTaskViewed() {
  // 无红点说明没有新上线任务，无需上报
  if (!activityEntry.value.newbieTaskRedDot) return;
  activityEntry.value = { ...activityEntry.value, newbieTaskRedDot: false };
  try {
    await reportNewbieTaskView({}, { hideMessage: true });
  } catch (err) {
    console.error('标记新手任务已查看失败：', err);
  }
}

/**
 * 清除当前用户的活动入口缓存。
 * @returns {void}
 */
export function resetActivityEntry() {
  requestVersion += 1;
  inflight = null;
  loaded = false;
  activityEntry.value = { ...DEFAULT_ENTRY };
}

/**
 * 读取活动入口可见性（响应式）并提供刷新方法。
 * @returns {{ activityEntry: import('vue').Ref<object>, fetchActivityEntry: typeof fetchActivityEntry }}
 * @example
 * const { activityEntry, fetchActivityEntry } = useActivityEntry();
 * fetchActivityEntry();
 */
export function useActivityEntry() {
  return { activityEntry, fetchActivityEntry };
}
